import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import ComplianceNotice from '../components/ComplianceNotice';
import { 
  ExclamationTriangleIcon, 
  PlusIcon, 
  ClockIcon, 
  UserIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

interface Issue {
  id: string;
  title: string;
  description: string;
  status: string;
  createdAt: string;
  assignedTo?: string;
}

const Issues: React.FC = () => {
  const { user } = useAuth();
  const [issues, setIssues] = useState<Issue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Issue['status'] | 'all'>('all');
  const [showNewIssueForm, setShowNewIssueForm] = useState(false);
  const [newIssue, setNewIssue] = useState({
    title: '',
    description: '',
    priority: 'MEDIUM'
  });

  const fetchIssues = useCallback(async () => {
    if (!user?.propertyGroupId) return;

    setLoading(true);
    try {
      // Backend automatically filters by propertyGroupId and user role
      const response = await api.get<Issue[]>('/api/issues');

      setIssues(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching issues:', error);
      setIssues([]);
      setError('Unable to load issues. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user?.propertyGroupId]);

  useEffect(() => {
    fetchIssues();
  }, [fetchIssues]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'OPEN': return 'text-red-400 bg-red-500/20';
      case 'IN_PROGRESS': return 'text-yellow-400 bg-yellow-500/20';
      case 'RESOLVED': return 'text-green-400 bg-green-500/20';
      default: return 'text-white/60 bg-white/10';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toUpperCase()) {
      case 'OPEN': return XCircleIcon;
      case 'IN_PROGRESS': return ClockIcon;
      case 'RESOLVED': return CheckCircleIcon;
      default: return ExclamationTriangleIcon;
    }
  };

  const filteredIssues = filter === 'all' 
    ? issues 
    : issues.filter(issue => issue.status.toUpperCase() === filter);

  // Issues are already filtered by HOA and user role in fetchIssues
  const displayIssues = filteredIssues;

  const handleSubmitIssue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.propertyGroupId && !user?.hoaId) return;
    
    try {
      await api.post('/api/issues', {
        title: newIssue.title,
        description: newIssue.description,
        priority: newIssue.priority
      });
      
      fetchIssues();
      setNewIssue({ title: '', description: '', priority: 'MEDIUM' });
      setShowNewIssueForm(false);
    } catch (error) {
      console.error('Error submitting issue:', error);
      alert('Failed to submit your request. Please try again.');
    }
  };

  const handleUpdateIssueStatus = async (issueId: string, newStatus: Issue['status']) => {
    if (user?.role !== 'ADMIN') return;

    // Optimistically update local state immediately (silent save)
    setIssues(prev => prev.map(issue =>
      issue.id === issueId ? { ...issue, status: newStatus } : issue
    ));

    try {
      await api.put(`/api/issues/${issueId}`, { status: newStatus });
    } catch (error) {
      console.error('Error updating issue status:', error);
      // Revert on failure
      fetchIssues();
    }
  };

  const handleDeleteIssue = async (issueId: string) => {
    if (user?.role !== 'ADMIN') return;
    if (!window.confirm('Are you sure you want to delete this issue?')) return;

    // Optimistically remove from list
    setIssues(prev => prev.filter(issue => issue.id !== issueId));

    try {
      await api.delete(`/api/issues/${issueId}`);
    } catch (error) {
      console.error('Error deleting issue:', error);
      // Revert on failure
      fetchIssues();
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">
            {user?.role === 'RESIDENT' ? 'My Service Requests' : 'Community Issues'}
          </h1>
          <p className="text-white/60">
            {user?.role === 'RESIDENT' 
              ? 'Track your submitted requests and their progress'
              : 'Manage and track all community issues and maintenance requests'
            }
          </p>
        </div>
        <button 
          onClick={() => setShowNewIssueForm(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <PlusIcon className="w-5 h-5" />
          <span>{user?.role === 'RESIDENT' ? 'Submit Request' : 'Add Issue'}</span>
        </button>
      </div>

      <ComplianceNotice />

      {error && (
        <div className="floating-card border border-red-500/30 bg-red-500/10">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Open Issues</p>
              <p className="text-2xl font-bold text-red-400 mt-1">
                {issues.filter(i => i.status.toUpperCase() === 'OPEN').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-red-500/20 to-orange-500/20 flex items-center justify-center">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">In Progress</p>
              <p className="text-2xl font-bold text-yellow-400 mt-1">
                {issues.filter(i => i.status.toUpperCase() === 'IN_PROGRESS').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 flex items-center justify-center">
              <ClockIcon className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </div>

        <div className="floating-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/60 text-sm font-medium">Resolved</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {issues.filter(i => i.status.toUpperCase() === 'RESOLVED').length}
              </p>
            </div>
            <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 flex items-center justify-center">
              <CheckCircleIcon className="w-6 h-6 text-green-400" />
            </div>
          </div>
        </div>

      </div>

      {/* Filters */}
      <div className="floating-card">
        <div className="flex flex-wrap gap-3">
          {['all', 'OPEN', 'IN_PROGRESS', 'RESOLVED'].map(issueStatus => (
            <button
              key={issueStatus}
              onClick={() => setFilter(issueStatus as Issue['status'] | 'all')}
              className={`
                px-4 py-2 rounded-xl text-sm font-medium transition-all duration-200
                ${filter === issueStatus 
                  ? 'bg-[#22d3ee] text-white shadow-lg shadow-[#22d3ee]/25' 
                  : 'bg-white/10 text-white/80 hover:bg-white/20'}
              `}
            >
              {issueStatus === 'all' ? 'All Issues' : issueStatus.replace('-', ' ').charAt(0).toUpperCase() + issueStatus.replace('-', ' ').slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Issues Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayIssues.map(issue => {
          const StatusIcon = getStatusIcon(issue.status);
          return (
            <div 
              key={issue.id} 
              className="floating-card group hover:scale-[1.02] transition-all duration-200"
            >
              <div className="flex items-start justify-between mb-4">
                <span className={`px-3 py-1 rounded-lg text-xs font-medium ${getStatusColor(issue.status)}`}>
                  {issue.status.replace('-', ' ').charAt(0).toUpperCase() + issue.status.replace('-', ' ').slice(1)}
                </span>
                {user?.role === 'ADMIN' ? (
                  <button
                    onClick={() => handleDeleteIssue(issue.id)}
                    className="text-white/40 hover:text-red-400 transition-colors"
                    title="Delete issue"
                    aria-label={`Delete issue ${issue.title}`}
                  >
                    <XCircleIcon className="w-5 h-5" />
                  </button>
                ) : (
                  <StatusIcon className="w-5 h-5 text-white/60" />
                )}
              </div>
              
              <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-[#22d3ee] transition-colors">
                {issue.title}
              </h3>
              
              <p className="text-white/60 text-sm mb-4 line-clamp-2">
                {issue.description}
              </p>
              
              <div className="space-y-2 text-xs text-white/40">
                <div className="flex items-center">
                  <ClockIcon className="w-4 h-4 mr-2" />
                  Created: {formatDate(issue.createdAt)}
                </div>
                {issue.assignedTo && (
                  <div className="flex items-center">
                    <UserIcon className="w-4 h-4 mr-2" />
                    Assigned to: {issue.assignedTo}
                  </div>
                )}
              </div>

              {user?.role === 'ADMIN' && (
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex space-x-2 mb-2">
                    <select
                      aria-label={`Change status of issue ${issue.title}`}
                      value={issue.status}
                      onChange={(e) => handleUpdateIssueStatus(issue.id, e.target.value as Issue['status'])}
                      className="input-field text-xs px-2 py-1 flex-1"
                    >
                      <option value="OPEN">Open</option>
                      <option value="IN_PROGRESS">In Progress</option>
                      <option value="RESOLVED">Resolved</option>
                    </select>
                  </div>
                  <p className="text-xs text-white/40">Admin Controls</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {loading && issues.length === 0 && !error && (
        <div className="floating-card text-center py-12">
          <ExclamationTriangleIcon className="w-16 h-16 text-white/20 mx-auto mb-4 animate-pulse" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">Loading issues…</h3>
        </div>
      )}

      {!loading && displayIssues.length === 0 && (
        <div className="floating-card text-center py-12">
          <ExclamationTriangleIcon className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white/60 mb-2">No issues found</h3>
          <p className="text-white/40">
            {error
              ? 'We couldn\'t load issues right now.'
              : filter === 'all'
                ? 'No issues have been reported yet'
                : 'No issues match this filter'}
          </p>
        </div>
      )}

      {/* New Issue Modal */}
      {showNewIssueForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-card w-full max-w-md">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white">
                {user?.role === 'RESIDENT' ? 'Submit Service Request' : 'Add New Issue'}
              </h2>
              <button 
                onClick={() => setShowNewIssueForm(false)}
                className="text-white/60 hover:text-white transition-colors"
              >
                <XCircleIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmitIssue} className="space-y-4">
              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Title</label>
                <input
                  type="text"
                  value={newIssue.title}
                  onChange={(e) => setNewIssue({...newIssue, title: e.target.value})}
                  className="input-field w-full"
                  placeholder="Brief description of the issue"
                  required
                />
              </div>


              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Priority</label>
                <select
                  value={newIssue.priority}
                  onChange={(e) => setNewIssue({...newIssue, priority: e.target.value})}
                  className="input-field w-full"
                >
                  <option value="LOW">Low</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="HIGH">High</option>
                  <option value="URGENT">Urgent</option>
                </select>
              </div>

              <div>
                <label className="block text-white/80 text-sm font-medium mb-2">Description</label>
                <textarea
                  value={newIssue.description}
                  onChange={(e) => setNewIssue({...newIssue, description: e.target.value})}
                  className="input-field w-full h-24 resize-none"
                  placeholder="Detailed description of the issue..."
                  required
                />
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowNewIssueForm(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary flex-1"
                >
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Issues; 